import { useState, useEffect, useCallback } from "react";
import { Loader2, Download, AlertCircle, CalendarDays, Package } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import DataSourceNotice from "@/components/DataSourceNotice";

import SCKPIDashboard from "@/components/supplychain/SCKPIDashboard";
import InventoryTable from "@/components/supplychain/InventoryTable";
import DeliveryTracking from "@/components/supplychain/DeliveryTracking";
import SupplyChainChat from "@/components/supplychain/SupplyChainChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplyChainAgent = () => {
  const [data, setData] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [periodDays, setPeriodDays] = useState(90);
  const [activeTab, setActiveTab] = useState("dashboard");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supply-chain-agent/data`, {
        params: { period_days: periodDays, include_inventory: false, summary_only: true },
      });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Access denied. Supply Chain Analyst role required.");
      } else {
        toast.error("Failed to load supply chain data");
      }
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadDeliveries = useCallback(async ({ dateFrom, dateTo }) => {
    setDeliveriesLoading(true);
    setDeliveries(null);
    try {
      const response = await axios.get(`${API}/supply-chain-agent/deliveries`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setDeliveries(response.data.deliveries);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load delivery details");
    } finally {
      setDeliveriesLoading(false);
    }
  }, []);

  const handleExport = async (reportType) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/supply-chain-agent/export/${reportType}`, {
        params: { period_days: periodDays },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `supply_chain_${reportType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="supply-chain-agent-page">
      <DataSourceNotice source={data?.data_source} message={data?.message} />
      {data?.data_issues?.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="sc-data-issues">
          <span className="font-semibold">Some SAP inputs are unavailable:</span>{" "}
          {data.data_issues.map((issue) => `${issue.endpoint.replaceAll("_", " ")}: ${issue.message}`).join("; ")}
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-green-600" />
            Supply Chain Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Inventory management, delivery tracking, and AI-powered supply chain insights
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data?.data_source === "mock" && (
            <Badge variant="secondary" className="gap-1 text-amber-700 bg-amber-50 border-amber-200" data-testid="sc-mock-data-badge">
              <AlertCircle className="w-3 h-3" /> Sample Data
            </Badge>
          )}
          {activeTab === "dashboard" && (
            <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-xs shadow-sm">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              <label htmlFor="sc-period-filter" className="sr-only">Period filter</label>
              <select
                id="sc-period-filter"
                value={periodDays}
                onChange={(event) => setPeriodDays(Number(event.target.value))}
                className="cursor-pointer bg-transparent font-medium text-foreground outline-none"
                data-testid="sc-period-filter"
              >
                <option value={30}>Last 30 Days</option>
                <option value={60}>Last 60 Days</option>
                <option value={90}>Last 90 Days</option>
                <option value={180}>Last 180 Days</option>
              </select>
            </div>
          )}
          <Button
            size="sm"
            className="hidden"
            onClick={fetchData}
            disabled={loading}
            data-testid="sc-go-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            GO
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("all")} disabled={exporting} data-testid="sc-export-all-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            Export All
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start" data-testid="sc-tabs">
          <TabsTrigger value="dashboard" data-testid="sc-tab-dashboard">Dashboard</TabsTrigger>

          <TabsTrigger value="inventory" data-testid="sc-tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="sc-tab-deliveries">Deliveries</TabsTrigger>
          <TabsTrigger value="analyst" data-testid="sc-tab-analyst">AI Analyst</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          {loading && !data ? (
            <div className="flex h-64 items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading dashboard KPIs from SAP...</div>
          ) : <SCKPIDashboard kpis={data?.kpis} reasons={data?.kpi_reasons} />}
        </TabsContent>



        <TabsContent value="inventory" className="mt-4">
          <InventoryTable />
        </TabsContent>

        <TabsContent value="deliveries" className="mt-4">
          <DeliveryTracking
            deliveries={deliveries}
            loading={deliveriesLoading}
            onLoad={loadDeliveries}
            onExport={() => handleExport("deliveries")}
            exporting={exporting}
          />
        </TabsContent>

        <TabsContent value="analyst" className="mt-4">
          <SupplyChainChat />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplyChainAgent;
